import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";

import { Form, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";

import { useProductOperations } from "../../hooks/inventario/useProductOperations";

import { icons } from "../../const/icons";
import { CustomModal } from "../Modals/CustomModal";
import { CustomConfirm } from "../Modals/CustomConfirm";

import "./InventarioAddImage.css";
import {
  extractBase64FromDataUrl,
  getFileExtension,
  isValidImageSize,
  isValidImageType,
} from "../../utils/util";

/**
 * MODAL: AGREGAR/ELIMINAR IMAGEN DE PRODUCTO
 * Permite gestionar la imagen de un producto con cambios locales
 * hasta que se confirmen explícitamente.
 */
export const InventarioAddImage = ({
  visible,
  onClose,
  onSubmit,
  onDelete,
  producto,
  onSuccess,
  saving = false,
  serverError = null,
}) => {
  const { states: productStates } = useProductOperations();
  const prevVisibleRef = useRef(false);

  const [form] = Form.useForm();
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  //* HELPERS
  const resetStates = () => {
    setCurrentImage(null);
    setSelectedFile(null);
    setError(null);
    setConfirmDeleteVisible(false);
    form.resetFields();
  };

  /**
   * Convierte archivo a base64
   */
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  /**
   * Trunca el nombre del archivo
   */
  const truncateFileName = (fileName, maxLength = 25) => {
    if (!fileName || fileName.length <= maxLength) return fileName;

    const lastDotIndex = fileName.lastIndexOf(".");
    const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : "";

    const extensionLength = extension.length;
    const availableLength = maxLength - extensionLength - 3;

    if (availableLength <= 0) {
      return fileName.substring(0, maxLength - 3) + "...";
    }

    const truncatedName = name.substring(0, availableLength);
    return truncatedName + "..." + extension;
  };

  //* EFFECTS
  // Inicializar cuando el modal se abre
  useEffect(() => {
    if (visible && !prevVisibleRef.current && producto) {
      setTimeout(() => {
        const initialImage = producto.imageUrl || null;
        setCurrentImage(initialImage);

        setSelectedFile(null);
        setError(null);
        form.resetFields();
      }, 0);
    }

    prevVisibleRef.current = visible;
  }, [visible, producto, form]);

  // sincronizar error
  useEffect(() => {
    if (serverError) {
      setTimeout(() => {
        setError(serverError);
      }, 0);
    }
  }, [serverError]);

  // limpieza
  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        resetStates();
        setError(null);
      }, 0);
    }
  }, [visible]);

  //*  HANDLERS
  /**
   * Cierra el modal sin guardar cambios
   */
  const handleCancel = () => {
    if (saving) return; // evitar cerrar mientras se guarda
    onClose();
  };

  /**
   * Guarda los cambios (nueva imagen, reemplazo o eliminación)
   */
  const handleOk = async () => {
    if (!producto || saving) return;

    // No hay cambios
    if (!selectedFile) {
      form.setFields([
        {
          name: "imagen",
          errors: ["Debe seleccionar una imagen."],
        },
      ]);
      return;
    }

    // Nueva imagen seleccionada
    try {
      const res = await onSubmit({
        productId: producto.id,
        fileName: selectedFile.fileName,
        base64: selectedFile.base64,
        contentType: selectedFile.contentType,
      });

      if (!res || res.error || res.success === false) {
        // No cerrar, mostrar error
        const message = res?.error || "Error al guardar la imagen en el servidor";
        setError(message);
        return;
      }

      setError(null);
      form.resetFields();
      onClose();

      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      setError(error?.message || "Error al procesar la imagen");
    }
  };

  const handleClickDelete = () => {
    setConfirmDeleteVisible(true);
  };

  // Confirmar eliminación en el segundo modal
  const handleConfirmDelete = async () => {
    if (!producto) return;

    if (!onDelete || typeof onDelete !== "function") {
      setError("Error: función de eliminación no disponible");
      setConfirmDeleteVisible(false);
      return;
    }

    try {
      const res = await onDelete(producto.id);

      if (res?.error) {
        const message = res?.error || "Error al eliminar la imagen en el servidor";
        setError(message);
        setConfirmDeleteVisible(false);
        return;
      }

      setCurrentImage(null);
      setSelectedFile(null);
      setConfirmDeleteVisible(false);
      onClose();

      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || "Error al eliminar la imagen");
      setConfirmDeleteVisible(false);
    }
  };

  /**
   * Maneja el cambio de archivo
   */
  const handleFileChange = async ({ file }) => {
    if (!file) return;

    if (!isValidImageType(file)) {
      form.setFields([
        {
          name: "imagen",
          errors: ["El archivo debe ser una imagen."],
        },
      ]);
      return;
    }

    if (!isValidImageSize(file)) {
      form.setFields([
        {
          name: "imagen",
          errors: ["La imagen debe pesar menos de 1MB."],
        },
      ]);
      return;
    }

    try {
      const dataUrl = await getBase64(file); // data:image/jpeg;base64,AAAA...
      setCurrentImage(dataUrl);

      // extraer solo la parte base64
      const pureBase64 = extractBase64FromDataUrl(dataUrl);

      // obtener la extension
      let extension = getFileExtension(file);

      setSelectedFile({
        rawFile: file,
        fileName: file.name,
        base64: pureBase64,
        contentType: extension,
      });

      setError(null);
    } catch (error) {
      setError(error?.message || "Error al cargar la imagen");
    }
  };

  //  DerivadosL
  const hasOriginalImage = Boolean(producto?.imageUrl);

  // Título del modal
  const modalTitle = hasOriginalImage ? "Reemplazar imagen" : "Agregar imagen";

  // Botón de cancelar/eliminar
  let cancelButtonText = "Cancelar";
  let cancelButtonDanger = false;
  let onCancelAction = handleCancel;

  if (hasOriginalImage && !selectedFile) {
    cancelButtonText = "Eliminar imagen";
    cancelButtonDanger = true;
    onCancelAction = handleClickDelete;
  }

  // Texto del campo de selección
  const displayFileName = selectedFile
    ? truncateFileName(selectedFile.fileName)
    : "Seleccione imagen";

  //  RENDER
  return (
    <>
      <CustomModal
        visible={visible}
        title={modalTitle}
        onClose={handleCancel}
        onOk={handleOk}
        okText={saving ? "Guardando..." : "Guardar"}
        cancelText={cancelButtonText}
        onCancel={onCancelAction}
        width="540px"
        compact={true}
        cancelButtonProps={{
          danger: cancelButtonDanger,
          disabled: saving,
        }}
        okButtonProps={{
          loading: saving,
          disabled: saving,
        }}
      >
        <div className="add-image-container">
          {/* Preview de la imagen */}
          <div className="add-image-preview">
            {currentImage ? (
              <img
                src={currentImage}
                alt={producto?.product || "preview"}
                className="add-image-thumb"
              />
            ) : (
              <div className="add-image-placeholder">
                <InboxOutlined className="add-image-placeholder-icon" />
                <span className="add-image-placeholder-text">
                  {hasOriginalImage ? "Sin vista previa" : "Sin imagen personalizada"}
                </span>
              </div>
            )}
          </div>

          {/* Formulario */}
          <div className="add-image-form">
            <span className="add-image-product-name">{producto?.nombre}</span>

            <Form form={form} layout="vertical">
              <Form.Item label="Seleccione la imagen" name="imagen" className="add-image-form-item">
                <Upload
                  name="file"
                  accept="image/jpeg,image/jpg,image/png"
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleFileChange}
                  disabled={saving}
                >
                  <div className="add-image-upload-button">
                    <span className="add-image-upload-text">{displayFileName}</span>
                    <span className="add-image-upload-action">Seleccione archivo</span>
                  </div>
                </Upload>
              </Form.Item>
              {error && <span className="error-msg">{error}</span>}
            </Form>
          </div>
        </div>
      </CustomModal>

      {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
      <CustomConfirm
        visible={confirmDeleteVisible}
        caption="Atención"
        description="Este ítem usará la imagen predeterminada hasta que se seleccione una personalizada."
        iconUrl={icons.atencion}
        okText="Aceptar"
        showCancel={false}
        confirmLoading={productStates.imageState.loading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteVisible(false)}
      />
    </>
  );
};

InventarioAddImage.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func,
  producto: PropTypes.object,
  saving: PropTypes.bool,
  serverError: PropTypes.string,
  onSuccess: PropTypes.func,
};
